import React, { useEffect, useMemo, useState } from 'react';
import HiddenUploadInput from './imageEditor/src/components/common/HiddenUploadInput';
import LazyImage from './LazyImage';
import VirtualizedGrid from './VirtualizedGrid';
import {
  flattenGraphicsManifest,
  loadGraphicsManifest,
} from './useGraphicsManifest';

function RiImageAddFill(props) {
  return (
    <svg stroke="currentColor" fill="currentColor" strokeWidth={0} viewBox="0 0 24 24" height="1em" width="1em" {...props}>
      <g>
        <path fill="none" d="M0 0h24v24H0z" />
        <path d="M21 15v3h3v2h-3v3h-2v-3h-3v-2h3v-3h2zm.008-12c.548 0 .992.445.992.993v9.349A5.99 5.99 0 0 0 20 13V5H4l.001 14 9.292-9.293a.999.999 0 0 1 1.32-.084l.093.085 3.546 3.55a6.003 6.003 0 0 0-3.91 7.743L2.992 21A.993.993 0 0 1 2 20.007V3.993A1 1 0 0 1 2.992 3h18.016zM8 7a2 2 0 1 1 0 4 2 2 0 0 1 0-4z" />
      </g>
    </svg>
  );
}

const GraphicsGalleryPanel = ({
  activeCategory,
  setActiveCategory,
  onSelectImage,
  onUploadClick,
  uploadInputRef,
  onUploadChange,
  isImporting = false,
  onCancel = undefined,
  onClose = undefined,
  selectedImage = '',
  className = '',
}) => {
  const [graphicsManifest, setGraphicsManifest] = useState(null);
  const [manifestError, setManifestError] = useState(null);
  const [scrollRoot, setScrollRoot] = useState(null);
  const [columnCount, setColumnCount] = useState(3);

  // Thumbnails are 73×73px + 6px gap → ~79px per column in the 280px sidebar panel.
  useEffect(() => {
    if (!scrollRoot) {
      return undefined;
    }
    const GRAPHIC_CELL_WIDTH = 79;
    const updateColumns = () => {
      const width = scrollRoot.clientWidth || 240;
      setColumnCount(Math.max(2, Math.min(3, Math.floor(width / GRAPHIC_CELL_WIDTH))));
    };
    updateColumns();
    const observer = new ResizeObserver(updateColumns);
    observer.observe(scrollRoot);
    return () => observer.disconnect();
  }, [scrollRoot]);

  useEffect(() => {
    let cancelled = false;
    loadGraphicsManifest()
      .then((manifest) => {
        if (!cancelled) {
          setGraphicsManifest(manifest);
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setManifestError(error.message || 'Failed to load graphics');
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const galleryItems = useMemo(() => {
    if (!graphicsManifest) {
      return [];
    }
    return flattenGraphicsManifest(graphicsManifest, activeCategory);
  }, [graphicsManifest, activeCategory]);

  const categories = useMemo(
    () => (graphicsManifest ? Object.keys(graphicsManifest) : []),
    [graphicsManifest],
  );

  return (
    <div className={`graphics-gallery-panel ${className}`.trim()}>
      <div className="respark_sidebar-options-card">
      <div className="graphics-images-content d-f-c">
        <div className="respark_graphics-panel-banner">
          <div className="respark_sidebar-category-row">
            <div className="categories-list" role="tablist" aria-label="Graphics categories">
              <button
                type="button"
                role="tab"
                aria-selected={activeCategory === 'All'}
                className={`category-chip ${activeCategory === 'All' ? 'active' : ''}`}
                onClick={() => setActiveCategory('All')}
              >
                All
              </button>
              {categories.map((imageCategory) => (
                <button
                  type="button"
                  role="tab"
                  key={imageCategory}
                  aria-selected={activeCategory === imageCategory}
                  title={imageCategory}
                  className={`category-chip ${activeCategory === imageCategory ? 'active' : ''}`}
                  onClick={() => setActiveCategory(imageCategory)}
                >
                  <span>{imageCategory}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="modal-containt" ref={setScrollRoot}>
          {manifestError && (
            <div className="empty-wrap">{manifestError}</div>
          )}

          {!manifestError && !graphicsManifest && (
            <div className="empty-wrap">Loading graphics…</div>
          )}

          {graphicsManifest && (
            <div className="image-category-wrap">
              <div className="bg-images-list-wrap">
                <VirtualizedGrid
                  items={galleryItems}
                  getKey={(item) => item.key}
                  columnCount={columnCount}
                  estimatedRowHeight={81}
                  rowGap={6}
                  scrollElement={scrollRoot}
                  measureRows={false}
                  overscan={2}
                  emptyMessage={<div className="empty-wrap">No graphics in this category</div>}
                  renderItem={(item) => (
                    <div
                      className={`img-wrap ${selectedImage === item.src ? 'active' : ''}`}
                      onClick={() => onSelectImage(item.src)}
                    >
                      <LazyImage
                        src={item.src}
                        alt={item.alt}
                        scrollRoot={scrollRoot}
                        waitForScrollRoot
                      />
                    </div>
                  )}
                />
              </div>
            </div>
          )}
        </div>

        <div className="btn-wrap">
          <div className="btn" onClick={isImporting ? undefined : onUploadClick}>
            {isImporting ? 'Importing...' : 'Upload from your computer'} <RiImageAddFill />
          </div>
          {/* {onCancel && (
            <div className="btn" onClick={onCancel}>
              Cancel <IoMdCloseCircleOutline />
            </div>
          )} */}
        </div>

        <HiddenUploadInput
          ref={uploadInputRef}
          onChange={isImporting ? undefined : onUploadChange}
          disabled={isImporting}
          multiple
        />
      </div>
      </div>
    </div>
  );
};

export default GraphicsGalleryPanel;
